import ReportHeader from "../../shared/components/ReportHeader";
import React from "react";
import Typography from "@mui/material/Typography";
import CustomGauge from "../../../../shared/highcharts/CustomGauge";
import BoxContent from "../../../../shared/ui/BoxContent";
import Grid from "@mui/material/Grid";
import {Chip, Stack} from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import CustomAreaBar from "../../../../shared/highcharts/CustomAreaBar";
import Icon from "../../../../shared/ui/Icon";
import {formatDurationRo} from "../../../../utils/functions";
import CustomDonutChart from "../../../../shared/highcharts/CustomDonutChart";
import {
    getAnswerBoxColor, getEmpathyText,
    getLevel,
    getLevelBgColor,
    getLevelColor,
    getLevelTitle,
    getLevelTitleColor
} from "../../shared/utils/functions";
import {useAuth} from "../../../../hooks/useAuth";
import CustomButton from "../../../../shared/ui/CustomButton";


const getStandardsWithLowestScore = (standards: any) => {
    let lowestStandards =[];
    if (standards.length > 0) {
        const minNota = Math.min(...standards.map((s: any) => s.nota));

        lowestStandards = standards.filter((s: any) => Number(s.nota) === minNota);
    }

    return lowestStandards;
}

const AnswersBox = ({size = "small", answerType}: {size?: "small" | "large", answerType: string}) => {
    return (
        <Box sx={{
            width: size === "small" ? '10px' : '30px',
            height: size === "small" ? '10px' : '30px',
            aspectRatio: '1/1',
            margin: 'auto',
            borderRadius: '3px',
            backgroundColor: getAnswerBoxColor(answerType),
        }}></Box>
    )
}

const ItemLegendCard = ({title, answerType}: {title: string, answerType: string}) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.5,
            }}
        >
            <AnswersBox answerType={answerType} size="small"/>
            <Typography variant="body2" ml={0.5}>{title}</Typography>
        </Box>
    )
}

const ItemsLegend = () => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 1.5,
            }}
        >
            <ItemLegendCard title="Corect" answerType="corect"/>
            <ItemLegendCard title="Greșit" answerType="gresit"/>
            <ItemLegendCard title="Din neatenție" answerType="din_neatentie"/>
            <ItemLegendCard title="Omis" answerType="omis"/>
        </Box>
    )
}

const StandardsReport = ({report, isPrinting = false} : {report: any, isPrinting?: boolean}) => {
    const {update} = useAuth();
    const lowestStandards = getStandardsWithLowestScore(report.learning_standards);
    const isTestStandard = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"].includes(report.grade_level);
    const nrNotPayingAttention = report.items.filter((item: any) => item.answer === "din_neatentie").length;
    const isNotPayingAttention = report.items.filter((item: any) => item.answer === "din_neatentie").length > 0;

    const handleShowMore = (
        title: string,
        content: string
    ) => {

        update("error", {
            show: true,
            closable: true,
            title: title,
            message:  <Box dangerouslySetInnerHTML={{__html: content}}></Box>,
            button: "Închide",
        });
    }

    return (
        <Box mb={5}>
            <ReportHeader
                locationUrl="raspunsuri"
                reportId={report.id}
                score={report.score}
                testDate={report.closed_at}
                testTypeId={1}
                subjectName={report.discipline}
                className={report.grade_level}
            />

            <Typography variant="h1" mb={2} mt={4}>Rezumat</Typography>

            <BoxContent color="light" >
                <Grid container spacing={1} justifyContent="center">
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <CustomGauge value={report.score} color={getLevelColor(report.score, false)}/>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                display: {xs: 'block', lg: 'flex'},
                            }}
                        >
                            <Chip
                                label={`${report.count_right_answers} din ${report.total_items}`}
                                size="medium"
                                variant="filled"
                                sx={{ mr: 1, mb: 1, backgroundColor: "primary.light"}}
                            />
                            <Chip
                                label={formatDurationRo(report.duration)}
                                size="medium"
                                variant="filled"
                                sx={{ mr: 1, mb: 1, backgroundColor: "primary.light"}}
                            />
                            <Chip
                                label={getLevel(report.score)}
                                size="medium"
                                variant="outlined"
                                sx={{ mr: 1, mb: 1, color: getLevelColor(report.score, true), borderColor: getLevelColor(report.score, false) }}
                            />
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                                mt: 1,
                                border: "1px solid",
                                borderColor: getLevelColor(report.score, false),
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: getLevelBgColor(report.score),
                                color: getLevelColor(report.score, true),
                            }}
                        >
                            {getEmpathyText(report.score, report.items)}
                        </Typography>
                    </Grid>
                </Grid>
            </BoxContent>

            <Grid container spacing={3} mt={0.5} sx={{breakAfter: "always", pageBreakAfter: "always"}}>
                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <BoxContent color="light">
                        <Grid container spacing={1}>
                            {report.items.map((item : any, index: number) => (
                                <Grid item xs={2} sm={1} md={2} lg={1} key={index}>
                                    <Box sx={{
                                        width: '30px',
                                        height: '30px',
                                        aspectRatio: '1/1',
                                        margin: 'auto',
                                        borderRadius: '5px',
                                        backgroundColor: getAnswerBoxColor(item.answer)
                                    }}></Box>
                                </Grid>
                            ))}
                        </Grid>
                       <ItemsLegend />
                    </BoxContent>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <BoxContent color="light">
                        <Typography variant="body2" color="text.disabled" textTransform="uppercase">Întrebări ușoare ratate</Typography>
                        <Typography variant="body1">{nrNotPayingAttention} {nrNotPayingAttention === 1 ? "întrebare ușoară ratată." : "întrebări ușoare ratate."}</Typography>
                        <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 1, width: '100%' }} />
                        <Typography variant="body2" color="text.disabled" textTransform="uppercase">Viteza de lucru</Typography>
                        <Typography variant="body1">{report.avg_answered_time} secunde pe întrebare</Typography>
                    </BoxContent>
                </Grid>
            </Grid>

            <Typography variant="h1" mb={2} mt={4}>Profil pe arii de competență</Typography>

            <Grid container spacing={3} sx={{breakAfter: "always", pageBreakAfter: "always"}}>
                {report.specific_competences.length > 0 ? (
                    report.specific_competences.map((competence: any, index: number) => (
                    <Grid
                        item
                        xs={12}
                        lg={6}
                        key={index}
                    >
                        <BoxContent color="light">
                            <Typography variant="body2" color={getLevelColor(competence.progress, true)}>{getLevel(competence.progress)}</Typography>
                            <Typography variant="body1" mb={1} mt={0.5}>{competence.name}</Typography>
                            <CustomAreaBar width={competence.progress * 10} bgColor={getLevelColor(competence.progress, false)}/>
                        </BoxContent>
                    </Grid>
                ))) : (
                    <Grid
                        item
                        xs={12}
                        lg={6}
                    >
                        <BoxContent color="light">
                            <Typography variant="body2" color={getLevelColor(report.score, true)}>{getLevel(report.score)}</Typography>
                            <Typography variant="body1" mb={1} mt={0.5}>{report.discipline}</Typography>
                            <CustomAreaBar width={report.score * 10} bgColor={getLevelColor(report.score, false)}/>
                        </BoxContent>
                    </Grid>
                )}

            </Grid>

            {isTestStandard ? (
                <Typography variant="h1" mb={2} mt={4}>Standarde de conținut</Typography>
            ) : (
                <Typography variant="h1" mb={2} mt={4}>Conținuturi</Typography>
            )}
            <div style={{breakAfter: "always", pageBreakAfter: "always"}}>
                <Grid container spacing={3} >
                    {report.learning_standards.map((standard : any, standardIndex : number) => (
                        <Grid
                            item
                            xs={12}
                            lg={6}
                            key={standardIndex}
                        >
                            <BoxContent color="light">
                                <Typography variant="body1" mb={1} mt={0.5}>
                                    {standard.name}
                                </Typography>
                                <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 0.5, width: '100%' }} />
                                {standard.descriptors.map((descriptor : any, descriptorIndex: number) => (
                                    <Box key={descriptorIndex} width="100%">
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box
                                                display="flex"
                                                sx={{opacity: descriptor.progress !== 100 ? 0.5 : 1}}
                                            >
                                                <Box mr={1}>
                                                    <Icon icon={descriptor.progress === 100 ? "fas circle-check" : "far circle"} fixedWidth={true} sx={{ fontSize: "1rem", color: descriptor.progress === 100 ? "success.main" : "text.disabled" }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="body1" sx={{fontWeight: '500', color: getLevelTitleColor(descriptor.difficulty_level, true)}}>
                                                        {descriptor.status ? descriptor.status : getLevelTitle(descriptor.difficulty_level)}
                                                    </Typography>
                                                    {report.specific_competences.length > 0 ? (
                                                        <Typography variant="body1" mb={1} mt={0.5}>
                                                            {descriptor.name}
                                                        </Typography>
                                                    ) : (
                                                        isPrinting ? (
                                                            <Box mb={1} mt={0.5}>
                                                                <Typography variant="body1" display="inline" dangerouslySetInnerHTML={{__html: descriptor.name}}></Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box mb={1} mt={0.5}>
                                                                <Typography variant="body1" display="inline" dangerouslySetInnerHTML={{__html: descriptor.name.substring(0, 200)}}></Typography>
                                                                ... <CustomButton buttonType="text" color="secondary.light" bgcolor="text" onClick={() => handleShowMore(standard.name, descriptor.name)}>Vezi mai mult</CustomButton>
                                                            </Box>
                                                        )

                                                    )}

                                                </Box>
                                            </Box>
                                            {descriptor.progress !== 100 && descriptor.progress !== 0 && (
                                                <Box ml={3}>
                                                    <CustomDonutChart value={descriptor.progress} color={getLevelTitleColor(descriptor.difficulty_level, true)}/>
                                                </Box>
                                            )}
                                        </Box>
                                        {standard.descriptors.length != descriptorIndex + 1 && (
                                            <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 1, width: '100%' }} />
                                        )}

                                    </Box>
                                ))}
                            </BoxContent>
                        </Grid>
                    ))}
                </Grid>
            </div>


            <Typography variant="h1" mb={2} mt={4}>Următorii pași</Typography>

            <BoxContent color="light">
                <>
                    {lowestStandards.map((standard: any, index: number) => (
                        <Stack direction="row" alignItems="center" key={index}  mb={lowestStandards.length > 0 ? 1 : 0}>
                            <Typography
                                variant="body1"
                                mr={2}
                                mb={0}
                                sx={{
                                    width: "30px",
                                    minWidth: "30px",
                                    height: "30px",
                                    borderRadius: "50%",
                                    backgroundColor: "background.paper",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                1
                            </Typography>
                            <Typography variant="body1">
                                Este nevoie de mai multă aprofundare pe conținutul „{standard.name}”: exerciții scurte și frecvente pe acest standard, 1–2 pe zi.
                            </Typography>
                        </Stack>
                    ))}
                </>

                <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 1, width: '100%' }} />
                <Stack direction="row" alignItems="center">
                    <Typography
                        variant="body1"
                        mr={2}
                        mb={0}
                        sx={{
                            width: "30px",
                            minWidth: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            backgroundColor: "background.paper",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {lowestStandards.length + 1}
                    </Typography>
                    <Typography variant="body1">
                        {isNotPayingAttention ? (
                            "Este nevoie de mai multă concentrare în citirea cerinței la întrebarea/întrebările greșite din neatenție."
                        ) : (
                            "Nu s-au înregistrat greșeli din neatenție - este nevoie de exercițiu constant."
                        )}
                    </Typography>
                </Stack>

                <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 1, width: '100%' }} />
                <Stack direction="row" alignItems="center">
                    <Typography
                        variant="body1"
                        mr={2}
                        mb={0}
                        sx={{
                            width: "30px",
                            minWidth: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            backgroundColor: "background.paper",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {lowestStandards.length + 2}
                    </Typography>
                    <Typography variant="body1">
                        Înainte de finalizarea testului se recomandă verificarea tuturor răspunsurilor oferite, în limita timpului rămas disponibil, pentru asigurarea corectitudinii acestora.
                    </Typography>
                </Stack>

                <Divider sx={{ borderColor: "divider", marginBottom: 1, marginTop: 1, width: '100%' }} />
                <Stack direction="row" alignItems="center">
                    <Typography
                        variant="body1"
                        mr={2}
                        mb={0}
                        sx={{
                            width: "30px",
                            minWidth: "30px",
                            height: "30px",
                            borderRadius: "50%",
                            backgroundColor: "background.paper",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {lowestStandards.length + 3}
                    </Typography>
                    <Typography variant="body1">
                        Se recomandă reluarea testării plecând de la conținuturile la care s-au obținut rezultate mai slabe.
                    </Typography>
                </Stack>
            </BoxContent>

        </Box>
    )
}

export default StandardsReport;